import React, { useState, useEffect } from 'react';
import Select from '@tencent/dui/lib/components/Select';
import i18n from '@i18next/i18n';
import style from './style.less';
import { ListItem } from '@tencent/workbench';
import { getFontFamilyConfigData } from '@src/framework/editor/src/js/constants/toolbar-data';
import { IQuickToolFontList } from '../../quick-tool-dialog-manager.interface';
import Checkbox from '@tencent/dui/lib/components/Checkbox';

interface Props {
    onFontChange: (fontFamilyInfo: FontFamilyInfo) => void;
    FontListModule: React.ReactNode;
    QuickToolFontList: IQuickToolFontList;
    fontFamilyInfo: FontFamilyInfo|undefined;
}

export interface FontFamilyInfo {
    titleFontEa?: string;
    titleFontLatin?: string;
    contentFontEa?: string;
    contentFontLatin?: string;
}

enum FontType {
    TITLE,
    CONTENT,
}

enum FontLanguageType {
    EA,
    LATIN,
}

export type IFontInfo = React.FC<Props>;
const FontInfo: IFontInfo = (props: Props) => {
    const fontFamilyList: ListItem[] = getFontFamilyConfigData();
    const { onFontChange, FontListModule, QuickToolFontList, fontFamilyInfo } = props;

    const [titleFontEa, setTitleFontEa] = useState<string|undefined>(fontFamilyInfo?.titleFontEa);
    const [titleFontLatin, setTitleFontLatin] = useState<string|undefined>(fontFamilyInfo?.titleFontLatin);
    const [contentFontEa, setContentFontEa] = useState<string|undefined>(fontFamilyInfo?.contentFontEa);
    const [contentFontLatin, setContentFontLatin] = useState<string|undefined>(fontFamilyInfo?.contentFontLatin);
    const [allFontSame, setAllFontSame] = useState<boolean>(true);

    const setFont = (fontType: FontType, fontLanguageType: FontLanguageType, fontFamily: string|undefined) => {
        if (fontType === FontType.TITLE) {
            if (fontLanguageType === FontLanguageType.EA) {
                if (allFontSame) {
                    setAllFont(fontFamily);
                } else {
                    setTitleFontEa(fontFamily);
                }
            } else {
                setTitleFontLatin(fontFamily);
            }
        } else {
            if (fontLanguageType === FontLanguageType.EA) {
                setContentFontEa(fontFamily);
            } else {
                setContentFontLatin(fontFamily);
            }
        }
    };

    const setAllFont = (font?: string) => {
        setTitleFontEa(font);
        setTitleFontLatin(font);
        setContentFontEa(font);
        setContentFontLatin(font);
    };

    const getSelcetFontName = (fontType: FontType, fontLanguageType: FontLanguageType) => {
        if (fontType === FontType.TITLE) {
            if (fontLanguageType === FontLanguageType.EA) {
                return titleFontEa;
            }
            return titleFontLatin;
        }
        if (fontLanguageType === FontLanguageType.EA) {
            return contentFontEa;
        }
        return contentFontEa;
    };

    const getFontName = (id: string|undefined) => {
        if (!id) {
            return i18n.t('未选择');
        }
        const font = fontFamilyList.find(item => item.id === id);
        if (font) {
            return font.content as string;
        }
        return id;
    };

    const handleSelectVisableChange = (visable: boolean, fontType: FontType, fontLanguageType: FontLanguageType) => {
        // dui的select单选组件没有清除的功能，在下次点击的时候清掉当前选中以达到清除的能力
        if (!visable) {
            return;
        }
        setFont(fontType, fontLanguageType, undefined);
    };

    const handleAllFontSameCheckedChange = (checked: boolean) => {
        setAllFontSame(checked);
        const sameFont = titleFontEa || titleFontLatin || contentFontEa || contentFontLatin;
        if (checked) {
            setAllFont(sameFont);
        } else {
            setAllFont(titleFontEa);
        }
    };

    const getFontList = (fontType: FontType, fontLanguageType: FontLanguageType) => <QuickToolFontList
        onFontChange={(fontFamily: string) => {
            setFont(fontType, fontLanguageType, fontFamily);
        }}
        selectedFontFamily={getSelcetFontName(fontType, fontLanguageType) || ''}
        FontListModule={FontListModule}
    >
    </QuickToolFontList>;

    const getSelectComponent = (
        fontType: FontType,
        fontLanguageType: FontLanguageType,
        fontId: string|undefined,
    ) => <Select
        className={style['font-select']}
        renderDropdown={() => getFontList(fontType, fontLanguageType)}
        listContainerClassName={style['list-container']}
        listStyle={{ padding: '0' }}
        placeholder={getFontName(fontId)}
        textStyle={{ color: '#000' }}
        onVisibleChange={(visbale) => {
            handleSelectVisableChange(visbale, fontType, fontLanguageType);
        }}
    >
    </Select>;

    useEffect(() => {
        onFontChange({
            titleFontEa,
            titleFontLatin,
            contentFontEa,
            contentFontLatin,
        });
    }, [titleFontEa, titleFontLatin, contentFontEa, contentFontLatin]);

    return (
        <div style={{ marginBottom: '20px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ width: '50%' }} className={style.subtitle}>{allFontSame ? i18n.t('所有文字') : i18n.t('标题')}</div>
                <Checkbox style={{ height: '20px' }} onChange={handleAllFontSameCheckedChange} defaultChecked>{i18n.t('所有文字字体相同')}</Checkbox>
            </div>
            <div className={style['font-select-wrapper']}>
                <span>{allFontSame ? i18n.t('目标字体') : i18n.t('中文字体')}</span>
                {getSelectComponent(FontType.TITLE, FontLanguageType.EA, titleFontEa)}
            </div>
            {
                !allFontSame
                && <div className={style['font-select-wrapper']}>
                    <span>{i18n.t('西文字体')}</span>
                    {getSelectComponent(FontType.TITLE, FontLanguageType.LATIN, titleFontLatin)}
                </div>
            }
            { !allFontSame && <div className={style.subtitle}>{i18n.t('正文')}</div> }
            {
                !allFontSame
                && <div className={style['font-select-wrapper']}>
                    <span>{i18n.t('中文字体')}</span>
                    {getSelectComponent(FontType.CONTENT, FontLanguageType.EA, contentFontEa)}
                </div>
            }
            {
                !allFontSame
                && <div className={style['font-select-wrapper']}>
                    <span>{i18n.t('西文字体')}</span>
                    {getSelectComponent(FontType.CONTENT, FontLanguageType.LATIN, contentFontLatin)}
                </div>
            }
        </div>
    );
};

export default FontInfo;
